import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Plus, X, Shuffle, ChevronDown, Users, Tag, Trash2, UserCheck, Briefcase, Crown, Pencil } from 'lucide-react';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';
import { useSiteContext } from '../../../context/SiteContext';

const SCROLL_EDGE_PX = 40;   // proximity zone near top/bottom edges
const SCROLL_SPEED = 8;      // pixels per frame

const ROOM_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500',
  'bg-pink-500', 'bg-cyan-500', 'bg-yellow-500', 'bg-red-500'
];

const AUTO_ASSIGN_CRITERIA = [
  { id: 'random', label: 'Random', description: 'Distribute participants randomly', icon: Shuffle },
  { id: 'byRole', label: 'By Role', description: 'Group by participant role', icon: Briefcase },
  { id: 'balancedMix', label: 'Balanced Mix', description: 'Mix roles evenly across rooms', icon: UserCheck },
  { id: 'byTag', label: 'By Tag/Topic', description: 'Group by interest tags', icon: Tag },
];

export default function SetupTab({ participants = [], onAssignmentChange }) {
  const [showAutoAssignOptions, setShowAutoAssignOptions] = useState(false);
  const [autoAssignPos, setAutoAssignPos] = useState(null);
  const autoAssignBtnRef = useRef(null);
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [dragOverRoom, setDragOverRoom] = useState(null);

  // Local room state — initialized from userRooms, then managed locally
  const [localRooms, setLocalRooms] = useState([]);
  const [assignments, setAssignments] = useState({}); // { participantId: roomIndex }
  // Inline rename state: the room id currently being edited + the draft value
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const skipSyncRef = useRef(false);
  // When true, suppress assignment sync until contextRooms confirms all participants cleared.
  // Unlike skipSyncRef (one-shot), this persists across multiple contextRooms changes.
  const clearPendingRef = useRef(false);
  const [isAddingRoom, setIsAddingRoom] = useState(false);
  const addingRoomRef = useRef(false);
  // When set, skip assignment sync until the pending room name appears in contextRooms.
  // This survives multiple intermediate session-updated events (unlike a one-shot boolean).
  const pendingAddRoomRef = useRef(null);
  // Ref to read current localRooms inside effects without triggering re-runs
  const localRoomsRef = useRef(localRooms);
  useEffect(() => { localRoomsRef.current = localRooms; }, [localRooms]);
  // Track room IDs that have been locally deleted but not yet confirmed by the server
  const pendingDeletesRef = useRef(new Set());
  // Refs for auto-scroll during drag
  const unassignedScrollRef = useRef(null);
  const roomsScrollRef = useRef(null);
  const scrollRafRef = useRef(null);

  // Auto-scroll a container when dragging near its top/bottom edges
  const handleDragAutoScroll = useCallback((e) => {
    const containers = [unassignedScrollRef.current, roomsScrollRef.current].filter(Boolean);
    let scrollTarget = null;
    let scrollDir = 0;

    for (const el of containers) {
      const rect = el.getBoundingClientRect();
      const y = e.clientY;
      if (y >= rect.top && y <= rect.bottom) {
        if (y - rect.top < SCROLL_EDGE_PX) {
          scrollTarget = el;
          scrollDir = -1; // scroll up
        } else if (rect.bottom - y < SCROLL_EDGE_PX) {
          scrollTarget = el;
          scrollDir = 1;  // scroll down
        }
        break;
      }
    }

    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    if (scrollTarget && scrollDir !== 0) {
      const step = () => {
        scrollTarget.scrollTop += scrollDir * SCROLL_SPEED;
        scrollRafRef.current = requestAnimationFrame(step);
      };
      scrollRafRef.current = requestAnimationFrame(step);
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const {
    session,
    rooms: contextRooms,
    addRoom,
    removeRoom,
    renameRoom,
    assignParticipant,
    unassignParticipant,
    autoAssign,
    clearAll,
    assignRoomHost,
    knownBreakoutParticipantsRef,
  } = useBreakoutRooms();

  const { user } = useSiteContext();
  const isBasicPlan = user?.plan !== 'Business';
  const maxRooms = isBasicPlan ? 1 : Infinity;

  // Clean up auto-scroll animation frame on unmount
  useEffect(() => () => { if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current); }, []);

  // Rooms are created dynamically in-session via "+ Add Room" button.
  // No pre-loading from dashboard rooms.

  // Synchronous init: if contextRooms has data but localRooms is empty, populate immediately.
  // This handles the common case where the host reopens the modal and session data is already available.
  // Using a ref-based guard so this only fires once per mount (avoids overwriting user edits).
  const didInitRef = useRef(false);
  if (!didInitRef.current && contextRooms.length > 0 && localRoomsRef.current.length === 0) {
    didInitRef.current = true;
    const rooms = contextRooms.map(cr => ({ id: cr.roomId, name: cr.roomName }));
    localRoomsRef.current = rooms;
    setLocalRooms(rooms);
    // Also init assignments from server state
    const initAssignments = {};
    contextRooms.forEach((cr) => {
      const localIdx = rooms.findIndex(lr => lr.id === cr.roomId);
      if (localIdx === -1) return;
      (cr.participants || []).forEach((p) => {
        initAssignments[String(p.odId)] = localIdx;
      });
    });
    if (Object.keys(initAssignments).length > 0) {
      setAssignments(initAssignments);
    }
  }

  // Clear local assignments when session is closed (breakout rooms ended)
  useEffect(() => {
    if (session?.status === 'closed') {
      setAssignments({});
    }
  }, [session?.status]);

  // Sync local rooms with backend context rooms.
  // Only depends on contextRooms — reads localRooms via ref to avoid cascading re-runs
  // when rooms are added locally (which would clear assignments).
  useEffect(() => {
    const current = localRoomsRef.current;
    if (contextRooms.length > 0 && current.length === 0) {
      // Initialize local rooms from context (rooms created externally, e.g. ToolsPanel)
      const rooms = contextRooms.map(cr => ({
        id: cr.roomId,
        name: cr.roomName,
      }));
      setLocalRooms(rooms);
      // Update ref synchronously so the assignment-sync effect (which runs next
      // in the same batch) sees the correct room IDs instead of stale ones.
      localRoomsRef.current = rooms;
    } else if (contextRooms.length > 0 && current.length > 0) {
      // Clean up pendingDeletes: if a room ID is gone from contextRooms, deletion is confirmed
      const contextIds = new Set(contextRooms.map(cr => cr.roomId));
      pendingDeletesRef.current.forEach(id => {
        if (!contextIds.has(id)) {
          pendingDeletesRef.current.delete(id);
        }
      });

      // Filter out context rooms that are pending local deletion
      const filteredContext = contextRooms.filter(cr => !pendingDeletesRef.current.has(cr.roomId));

      // Match local rooms to filtered context rooms by ID first, then name for temp rooms
      const usedContextIds = new Set();
      const updated = [];
      for (const lr of current) {
        const isTempId = lr.id.startsWith('room-new-');
        // Try matching by ID first (most reliable)
        let match = filteredContext.find(cr => cr.roomId === lr.id && !usedContextIds.has(cr.roomId));
        // For temp IDs, try matching by name
        if (!match && isTempId) {
          match = filteredContext.find(cr => cr.roomName === lr.name && !usedContextIds.has(cr.roomId));
        }
        if (match) {
          usedContextIds.add(match.roomId);
          updated.push({ ...lr, id: match.roomId, name: match.roomName });
        } else if (isTempId) {
          // Temp room not yet on server — keep it
          updated.push(lr);
        }
        // Non-temp room with no server match was deleted server-side — drop it
      }

      // Append context rooms not matched to any local room (excluding pending deletes)
      const newRooms = filteredContext
        .filter(cr => !usedContextIds.has(cr.roomId))
        .map(cr => ({ id: cr.roomId, name: cr.roomName }));

      const merged = [...updated, ...newRooms];

      // Only setState if something actually changed
      const changed = merged.length !== current.length ||
        merged.some((r, i) => r.id !== current[i]?.id || r.name !== current[i]?.name);
      if (changed) {
        setLocalRooms(merged);
        localRoomsRef.current = merged;
      }
    }
  }, [contextRooms]);

  // Sync local assignments from server state (contextRooms) to keep UI in sync.
  // Only depends on contextRooms — reads localRooms via ref.
  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    // Skip assignment sync while a room add is pending (waiting for server confirmation).
    // Clear the pending state once the room name appears in contextRooms.
    if (pendingAddRoomRef.current) {
      const confirmed = contextRooms.some(cr => cr.roomName === pendingAddRoomRef.current);
      if (confirmed) {
        pendingAddRoomRef.current = null;
      }
      // Skip this sync whether confirmed or not — the room sync in the same
      // effect batch just updated localRoomsRef with new IDs, so let the next
      // contextRooms change (if any) run the assignment sync with stable state.
      return;
    }
    // After Clear All, keep suppressing sync until server confirms rooms are empty.
    // This prevents stale session-updated events from re-assigning participants.
    if (clearPendingRef.current) {
      const hasAnyParticipants = contextRooms.some(cr => (cr.participants || []).length > 0);
      if (!hasAnyParticipants) {
        // Server confirmed all rooms are empty — clear the pending flag
        clearPendingRef.current = false;
      }
      return;
    }
    const current = localRoomsRef.current;
    if (contextRooms.length > 0 && current.length > 0) {
      const serverAssignments = {};
      contextRooms.forEach((cr) => {
        // Skip rooms that are pending local deletion — their participants
        // were already moved to unassigned locally and the server hasn't
        // confirmed the delete yet, so re-assigning here would undo it.
        if (pendingDeletesRef.current.has(cr.roomId)) return;
        const localIdx = current.findIndex(lr => lr.id === cr.roomId);
        if (localIdx === -1) return;
        (cr.participants || []).forEach((p) => {
          serverAssignments[String(p.odId)] = localIdx;
        });
      });
      // Only update if different from current local assignments
      setAssignments(prev => {
        const prevKeys = Object.keys(prev).sort().join(',');
        const newKeys = Object.keys(serverAssignments).sort().join(',');
        const prevVals = Object.keys(prev).sort().map(k => prev[k]).join(',');
        const newVals = Object.keys(serverAssignments).sort().map(k => serverAssignments[k]).join(',');
        if (prevKeys === newKeys && prevVals === newVals) return prev;
        return serverAssignments;
      });
    }
  }, [contextRooms]);

  // Track breakout room participants so they remain visible after Clear All.
  // knownBreakoutParticipantsRef (from context) survives tab switches.
  // We maintain a local snapshot (state) that drives the UI — only updated through
  // explicit effects so intermediate re-renders can't cause flicker.
  const [breakoutExtras, setBreakoutExtras] = useState(() => {
    // On mount, seed from the context ref (may have entries from a previous tab visit)
    const liveIds = new Set(participants.map(p => String(p.id)));
    const initial = [];
    knownBreakoutParticipantsRef.current.forEach((p, key) => {
      if (!liveIds.has(key)) initial.push(p);
    });
    return initial;
  });

  // Accumulate: when contextRooms has participants not in the live Agora channel, remember them
  useEffect(() => {
    const liveIds = new Set(participants.map(p => String(p.id)));
    const ref = knownBreakoutParticipantsRef.current;
    let changed = false;
    contextRooms.forEach(room => {
      (room.participants || []).forEach(p => {
        const key = String(p.odId);
        if (!liveIds.has(key) && !ref.has(key)) {
          ref.set(key, {
            id: p.odId,
            displayName: p.odName || String(p.odId),
            name: p.odName || String(p.odId),
            role: p.role || 'participant',
            isInBreakoutRoom: true,
          });
          changed = true;
        }
      });
    });
    if (changed) {
      const extras = [];
      ref.forEach((p, key) => { if (!liveIds.has(key)) extras.push(p); });
      setBreakoutExtras(extras);
    }
  }, [contextRooms, participants, knownBreakoutParticipantsRef]);

  // Clean up: remove entries for participants who returned to the main channel
  useEffect(() => {
    const liveIds = new Set(participants.map(p => String(p.id)));
    const ref = knownBreakoutParticipantsRef.current;
    let changed = false;
    for (const key of ref.keys()) {
      if (liveIds.has(key)) {
        ref.delete(key);
        changed = true;
      }
    }
    if (changed) {
      const extras = [];
      ref.forEach((p, key) => { if (!liveIds.has(key)) extras.push(p); });
      setBreakoutExtras(extras);
    }
  }, [participants, knownBreakoutParticipantsRef]);

  // Merge live participants with known breakout participants
  const mergedParticipants = useMemo(() => {
    return [...participants, ...breakoutExtras];
  }, [participants, breakoutExtras]);

  // Only the meeting host/moderator is locked to the main room.
  // Co-hosts CAN be assigned to a breakout room — when assigned, the backend
  // auto-promotes them to that breakout's room host (see breakoutRoom.js).
  const isHostRole = (role) => ['host', 'moderator'].includes(role);

  // Compute unassigned participants (hosts still appear in the list but not in the count)
  const assignedSet = new Set(Object.keys(assignments));
  const unassigned = mergedParticipants.filter(p =>
    !assignedSet.has(String(p.id))
  );
  // Count only assignable (non-host) participants for the header
  const unassignedCount = unassigned.filter(p => !isHostRole(p.role) && !p.isLocal).length;

  // Report local assignment count to parent (BreakoutRoomModal) so "Open Breakout Rooms"
  // button enables immediately after auto-assign, without waiting for server sync.
  const assignedCount = Object.keys(assignments).length;
  useEffect(() => {
    onAssignmentChange?.(assignedCount);
  }, [assignedCount, onAssignmentChange]);

  // Get participants assigned to a specific room
  const getRoomParticipants = (roomIndex) => {
    return mergedParticipants.filter(p => assignments[String(p.id)] === roomIndex);
  };

  // Drag handlers
  const handleDragStart = useCallback((e, participant) => {
    setDraggedParticipant(participant);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(participant.id));
    // Stop auto-scroll when drag ends (drop outside, Escape, etc.)
    const onDragEnd = () => { stopAutoScroll(); e.target.removeEventListener('dragend', onDragEnd); };
    e.target.addEventListener('dragend', onDragEnd);
  }, [stopAutoScroll]);

  const handleDragOver = useCallback((e, roomIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (roomIndex !== undefined) setDragOverRoom(roomIndex);
    handleDragAutoScroll(e);
  }, [handleDragAutoScroll]);

  const handleDragLeave = useCallback(() => {
    setDragOverRoom(null);
  }, []);

  const handleDrop = useCallback((e, roomIndex) => {
    e.preventDefault();
    setDragOverRoom(null);
    stopAutoScroll();
    if (draggedParticipant) {
      // Prevent hosts from being assigned to breakout rooms
      if (isHostRole(draggedParticipant.role)) {
        setDraggedParticipant(null);
        return;
      }
      const pid = String(draggedParticipant.id);
      // Update local state immediately (backend handles unassign-from-old-room automatically)
      setAssignments(prev => ({ ...prev, [pid]: roomIndex }));
      const room = localRooms[roomIndex];
      if (room) {
        // Backend's assign handler removes from all other rooms first,
        // so no explicit unassign needed (avoids race condition)
        const isSessionActive = session?.status === 'active';
        assignParticipant(
          draggedParticipant.id,
          draggedParticipant.displayName || draggedParticipant.name,
          draggedParticipant.role || 'participant',
          room.id,
          isSessionActive // only move immediately if session is already active
        );
      }
      setDraggedParticipant(null);
    }
  }, [draggedParticipant, localRooms, assignParticipant, stopAutoScroll]);

  const handleDropToUnassigned = useCallback((e) => {
    e.preventDefault();
    stopAutoScroll();
    if (draggedParticipant) {
      setAssignments(prev => {
        const next = { ...prev };
        delete next[String(draggedParticipant.id)];
        return next;
      });
      unassignParticipant(draggedParticipant.id, session?.status === 'active');
      setDraggedParticipant(null);
    }
  }, [draggedParticipant, unassignParticipant, stopAutoScroll]);

  const handleUnassign = useCallback((participantId) => {
    setAssignments(prev => {
      const next = { ...prev };
      delete next[String(participantId)];
      return next;
    });
    unassignParticipant(participantId, session?.status === 'active');
  }, [unassignParticipant, session]);

  const handleAutoAssign = useCallback((method) => {
    // Filter out hosts from auto-assignment
    const assignable = unassigned.filter(p => !isHostRole(p.role));

    const participantData = assignable.map(p => ({
      odId: p.id,
      odName: p.displayName || p.name,
      role: p.role || 'participant',
      tags: p.tags || [],
    }));
    // Let the server handle assignment — the session-updated event will sync
    // local assignments via the contextRooms sync effect.
    autoAssign(method, participantData);
    setShowAutoAssignOptions(false);
  }, [unassigned, autoAssign]);

  const handleAddRoom = useCallback(() => {
    if (addingRoomRef.current) return;
    if (localRoomsRef.current.length >= maxRooms) return;
    addingRoomRef.current = true;
    pendingAddRoomRef.current = null; // will be set below after computing room name
    setIsAddingRoom(true);

    // Compute next room name from both local AND server rooms to avoid duplicates
    // (local may have stale names if server renumbered after a deletion)
    const current = localRoomsRef.current;
    const usedNums = new Set();
    current.forEach(r => {
      const m = r.name.match(/^Room (\d+)$/);
      if (m) usedNums.add(parseInt(m[1]));
    });
    contextRooms.forEach(cr => {
      const m = cr.roomName.match(/^Room (\d+)$/);
      if (m) usedNums.add(parseInt(m[1]));
    });
    let nextNum = 1;
    while (usedNums.has(nextNum)) nextNum++;
    const roomName = `Room ${nextNum}`;

    // Preserve existing assignments: skip assignment sync until this room is confirmed by the server.
    // Unlike a one-shot boolean, this survives multiple intermediate session-updated events.
    pendingAddRoomRef.current = roomName;

    // Emit to backend (side effect kept outside state updater)
    addRoom(roomName);

    // Update local state
    setLocalRooms(prev => [...prev, {
      id: `room-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: roomName,
    }]);

    setTimeout(() => { addingRoomRef.current = false; setIsAddingRoom(false); }, 600);
  }, [addRoom, contextRooms, maxRooms]);

  const startEditingRoom = useCallback((room) => {
    setEditingRoomId(room.id);
    setEditingName(room.name);
  }, []);

  const cancelEditingRoom = useCallback(() => {
    setEditingRoomId(null);
    setEditingName('');
  }, []);

  const commitEditingRoom = useCallback(() => {
    const id = editingRoomId;
    const trimmed = editingName.trim();
    if (!id) return;
    const room = localRoomsRef.current.find(r => r.id === id);
    if (!room) { cancelEditingRoom(); return; }
    if (!trimmed || trimmed === room.name) { cancelEditingRoom(); return; }

    // Optimistic local update — the server broadcast will confirm via the sync effect
    setLocalRooms(prev => prev.map(r => r.id === id ? { ...r, name: trimmed } : r));

    // Only emit to server if this room already exists on the server (non-temp id)
    const isTempId = String(id).startsWith('room-new-');
    if (!isTempId) {
      renameRoom(id, trimmed);
    }
    cancelEditingRoom();
  }, [editingRoomId, editingName, renameRoom, cancelEditingRoom]);

  const handleRemoveRoom = useCallback((roomIndex) => {
    const room = localRooms[roomIndex];
    if (room) {
      // Resolve the real server ID — the local room may still have a temp ID
      const serverRoom = contextRooms.find(cr => cr.roomId === room.id)
        || contextRooms.find(cr => cr.roomName === room.name)
        || contextRooms[roomIndex];
      const realId = serverRoom?.roomId || room.id;

      // Track this deletion so the sync effect doesn't re-add the room
      pendingDeletesRef.current.add(realId);
      if (room.id !== realId) {
        pendingDeletesRef.current.add(room.id);
      }

      // Skip assignment sync for this server update (we're adjusting indices locally)
      skipSyncRef.current = true;

      // Move assigned participants back to unassigned
      setAssignments(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(pid => {
          if (next[pid] === roomIndex) delete next[pid];
        });
        // Adjust indices for rooms after the removed one
        const adjusted = {};
        Object.entries(next).forEach(([pid, idx]) => {
          adjusted[pid] = idx > roomIndex ? idx - 1 : idx;
        });
        return adjusted;
      });

      // Clear knownBreakoutParticipantsRef entries for participants in the
      // deleted room so they don't linger as phantom "in breakout" entries.
      if (serverRoom?.participants) {
        serverRoom.participants.forEach(p => {
          knownBreakoutParticipantsRef.current.delete(String(p.odId));
        });
        // Recompute breakoutExtras from the updated ref
        const liveIds = new Set(participants.map(p => String(p.id)));
        const extras = [];
        knownBreakoutParticipantsRef.current.forEach((p, key) => {
          if (!liveIds.has(key)) extras.push(p);
        });
        setBreakoutExtras(extras);
      }

      // Remove the room locally; backend will renumber and sync effect will propagate new names
      setLocalRooms(prev => prev.filter((_, i) => i !== roomIndex));
      removeRoom(realId);
    }
  }, [localRooms, contextRooms, removeRoom, participants, knownBreakoutParticipantsRef]);

  const handleClearAll = useCallback(() => {
    skipSyncRef.current = true;
    clearPendingRef.current = true;
    setAssignments({});
    clearAll();
  }, [clearAll]);

  return (
    // Let the parent flex chain (BreakoutRoomModal → tab content area) own the
    // height. The previous inline `maxHeight: calc(90vh - 220px)` was a magic
    // number that fought the flex layout and pushed the modal footer (Open
    // Rooms button) off-screen on mobile.
    <div className="flex flex-col md:flex-row gap-4 md:gap-6 h-full min-h-0">
      {/* Unassigned Participants — bound to ~35vh on mobile so the rooms
          list below stays visible without scrolling the whole modal. */}
      <div
        className="w-full md:w-64 flex flex-col min-h-0 shrink-0 max-h-[35vh] md:max-h-full"
        onDragOver={(e) => handleDragOver(e)}
        onDrop={handleDropToUnassigned}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-400">
            Unassigned ({unassignedCount})
          </h3>
          <div>
            <button
              ref={autoAssignBtnRef}
              onClick={() => {
                if (!showAutoAssignOptions && autoAssignBtnRef.current) {
                  const rect = autoAssignBtnRef.current.getBoundingClientRect();
                  setAutoAssignPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                }
                setShowAutoAssignOptions(!showAutoAssignOptions);
              }}
              className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 transition-colors"
            >
              <Shuffle className="w-3 h-3" />
              Auto-assign
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* Auto-assign Options Dropdown */}
            {showAutoAssignOptions && autoAssignPos && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowAutoAssignOptions(false)} />
                <div
                  className="fixed w-56 bg-[#001233] border border-white/10 rounded-xl shadow-xl z-50 py-2 max-h-64 overflow-y-auto"
                  style={{ top: autoAssignPos.top, right: autoAssignPos.right }}
                >
                  <p className="px-3 py-1 text-[10px] text-gray-500 uppercase tracking-wider">Assignment Method</p>
                  {AUTO_ASSIGN_CRITERIA.map((criteria) => {
                    const Icon = criteria.icon;
                    return (
                      <button
                        key={criteria.id}
                        onClick={() => handleAutoAssign(criteria.id)}
                        className="w-full px-3 py-2 flex items-start gap-3 hover:bg-white/10 transition-colors text-left"
                      >
                        <Icon className="w-4 h-4 text-indigo-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-white font-medium">{criteria.label}</p>
                          <p className="text-[10px] text-gray-500">{criteria.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div ref={unassignedScrollRef} className="flex-1 overflow-y-auto border border-white/10 rounded-xl p-3 bg-white/5">
          {unassigned.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">
              All participants assigned
            </p>
          ) : (
            <div className="space-y-2">
              {unassigned.map((p) => {
                const name = p.displayName || p.name || 'Guest';
                const isHost = isHostRole(p.role);
                return (
                  <div
                    key={p.id}
                    draggable={!isHost}
                    onDragStart={isHost ? undefined : (e) => handleDragStart(e, p)}
                    className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                      isHost
                        ? 'bg-white/5 opacity-60 cursor-default'
                        : 'bg-white/5 cursor-move hover:bg-white/10'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0 ${
                      isHost ? 'bg-yellow-600' : 'bg-gray-600'
                    }`}>
                      {isHost ? <Crown className="w-3.5 h-3.5" /> : name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-white break-words flex-1 min-w-0">{name}</span>
                    {isHost && (
                      <span className="text-[10px] text-yellow-400/70 font-medium uppercase tracking-wide flex-shrink-0">Host</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Rooms */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0" style={{ maxHeight: '100%' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-gray-400">
              Rooms ({localRooms.length}{isBasicPlan ? `/${maxRooms}` : ''})
            </h3>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
              isBasicPlan
                ? 'bg-gray-500/20 text-gray-400'
                : 'bg-[#00c853]/20 text-[#00c853]'
            }`}>
              {isBasicPlan ? 'Basic' : 'Business'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleClearAll}
              className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-white/5 transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={handleAddRoom}
              disabled={isAddingRoom || localRooms.length >= maxRooms}
              className={`text-xs text-white border border-white/20 px-3 py-1 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-1 ${isAddingRoom || localRooms.length >= maxRooms ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={localRooms.length >= maxRooms && isBasicPlan ? 'Upgrade to Business plan for unlimited breakout rooms' : ''}
            >
              <Plus className="w-3 h-3" />
              Add Room
            </button>
          </div>
        </div>

        {isBasicPlan && localRooms.length >= maxRooms && (
          <div className="mb-3 px-3 py-2 bg-[#6a1fbf]/10 border border-[#6a1fbf]/20 rounded-lg flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-300">Basic plan allows 1 breakout room.</span>
            <span className="text-xs text-[#6a1fbf] font-medium">Upgrade to Business for unlimited.</span>
          </div>
        )}

        <div ref={roomsScrollRef} className="flex-1 overflow-y-auto min-h-0">
          {localRooms.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Users className="w-8 h-8 text-gray-600 mb-2" />
              <p className="text-gray-500 text-sm">No rooms created yet</p>
              <p className="text-gray-600 text-xs mt-1">Click "+ Add Room" to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {localRooms.map((room, idx) => {
                const roomParticipants = getRoomParticipants(idx);
                const isOver = dragOverRoom === idx;
                const contextRoom = contextRooms.find(cr => cr.roomId === room.id);
                const moderatorId = contextRoom?.moderatorId;
                const moderatorName = moderatorId
                  ? (roomParticipants.find(p => String(p.id) === String(moderatorId))?.displayName
                    || roomParticipants.find(p => String(p.id) === String(moderatorId))?.name
                    || null)
                  : null;

                return (
                  <div
                    key={room.id}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, idx)}
                    className={`border rounded-xl overflow-hidden bg-white/5 transition-colors ${
                      isOver
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : 'border-white/10'
                    }`}
                  >
                    {/* Colored top bar */}
                    <div className={`h-1 ${ROOM_COLORS[idx % ROOM_COLORS.length]}`} />

                    <div className="p-3">
                      {/* Room Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          {editingRoomId === room.id ? (
                            <input
                              autoFocus
                              type="text"
                              value={editingName}
                              maxLength={60}
                              onChange={(e) => setEditingName(e.target.value)}
                              onBlur={commitEditingRoom}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); commitEditingRoom(); }
                                else if (e.key === 'Escape') { e.preventDefault(); cancelEditingRoom(); }
                              }}
                              className="bg-white/10 border border-indigo-400/60 focus:border-indigo-400 focus:outline-none text-white text-sm font-medium rounded px-1.5 py-0.5 min-w-0 flex-1"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => startEditingRoom(room)}
                              title="Click to rename"
                              className="group flex items-center gap-1 min-w-0 text-left hover:bg-white/5 rounded px-1 -mx-1 py-0.5 transition-colors"
                            >
                              <span className="text-white font-medium text-sm truncate">{room.name}</span>
                              <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0" />
                            </button>
                          )}
                          {moderatorName && (
                            <span className="text-[10px] text-yellow-400 flex items-center gap-0.5 flex-shrink-0" title={`Room Host: ${moderatorName}`}>
                              <Crown className="w-2.5 h-2.5" />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {roomParticipants.length}
                          </span>
                          {localRooms.length > 1 && (
                            <button
                              onClick={() => handleRemoveRoom(idx)}
                              className="p-1 hover:bg-red-500/20 rounded text-red-400"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Room Content */}
                      <div className="min-h-[80px] space-y-1">
                        {roomParticipants.length === 0 ? (
                          <p className="text-gray-500 text-xs text-center py-4">
                            Drag participants here
                          </p>
                        ) : (
                          roomParticipants.map((p) => {
                            const name = p.displayName || p.name || 'Guest';
                            const isModerator = moderatorId && String(moderatorId) === String(p.id);
                            return (
                              <div
                                key={p.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, p)}
                                className="flex items-center gap-1.5 p-1.5 bg-white/5 rounded cursor-move hover:bg-white/10 transition-colors group"
                              >
                                <div className="relative w-5 h-5 flex-shrink-0">
                                  <div className="w-5 h-5 rounded-full bg-gray-600 flex items-center justify-center text-[10px] font-medium text-white">
                                    {name.charAt(0).toUpperCase()}
                                  </div>
                                  {p.isInBreakoutRoom && (
                                    <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-[#001233]" title="In breakout room" />
                                  )}
                                </div>
                                <span className="text-xs text-white break-words flex-1 min-w-0">{name}</span>
                                <div className="flex items-center flex-shrink-0">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (contextRoom) {
                                        assignRoomHost(contextRoom.roomId, isModerator ? null : String(p.id));
                                      }
                                    }}
                                    className={`p-0.5 rounded transition-all ${
                                      isModerator
                                        ? 'opacity-100 hover:bg-red-500/20'
                                        : 'opacity-0 group-hover:opacity-100 hover:bg-yellow-500/20'
                                    }`}
                                    title={isModerator ? 'Remove as Room Host' : 'Make Room Host'}
                                  >
                                    <Crown className={`w-3 h-3 ${isModerator ? 'text-yellow-400' : 'text-gray-400'}`} />
                                  </button>
                                  <button
                                    onClick={() => handleUnassign(p.id)}
                                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/10 rounded transition-all"
                                  >
                                    <X className="w-3 h-3 text-gray-400" />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
